import apiV2 from "api-v2";
import type {
	EvaluationState,
	EvaluationStepNumber,
	StartEvaluationPayload,
	UpdateEvaluationStepPayload,
} from "../types/evaluation.type";
import type { PanelMode } from "../types/panel.type";
import { EVALUATION_STEP_META } from "../constants/evaluation.constants";
import type { EvaluationStepStatus } from "../types/evaluation.type";
import { PanelMemberStatus } from "../types/panel.type";

interface BackendCurrentUserContext {
	is_lead_scoring: boolean;
	is_rfx_lead: boolean;
	panel_member_id: number | null;
	panel_member_status: string | null;
}

interface BackendEvaluationResponse {
	id: number;
	ai_processing_cost: number;
	created_at: string;
	current_step: number;
	current_user_context?: BackendCurrentUserContext;
	evaluated_submissions: number;
	evaluation_completed_at: string | null;
	evaluation_phase: string;
	evaluation_started_at: string | null;
	evaluation_status: string;
	has_report: boolean;
	panel_mode: PanelMode | null;
	progress_percentage: number;
	rfx_id: number;
	total_submissions: number;
	uuid: string;
	workflow_config?: { steps: number[] } | null;
}

const mapBackendToEvaluationState = (
	data: BackendEvaluationResponse
): EvaluationState => {
	const currentStep = (data.current_step ?? 0) as EvaluationStepNumber;

	const steps = EVALUATION_STEP_META.map((meta, index) => {
		let stepStatus: EvaluationStepStatus = "not_started";
		if (index < currentStep) {
			stepStatus = "completed";
		} else if (index === currentStep) {
			stepStatus = "in_progress";
		}
		return {
			id: meta.id,
			name: meta.name,
			status: stepStatus,
		};
	});

	return {
		id: data.id,
		createdAt: data.created_at,
		currentStep,
		currentUserContext: data.current_user_context
			? {
					isRfxLead: data.current_user_context.is_rfx_lead,
					panelMemberId: data.current_user_context.panel_member_id,
					panelMemberStatus: data.current_user_context
						.panel_member_status as PanelMemberStatus,
					isLeadScoring: data.current_user_context.is_lead_scoring,
				}
			: undefined,
		hasStarted: true,
		panelMode: data.panel_mode,
		rfxId: data.rfx_id,
		steps,
		workflowConfig: data.workflow_config ?? undefined,
	};
};

export const getEvaluationState = async (
	rfxId: number,
	rfxSubmissionId?: number
): Promise<EvaluationState | null> => {
	const params = rfxSubmissionId ? { rfxSubmissionId } : {};
	const response = await apiV2.get(`/rfx/${rfxId}/evaluation/status`, {
		params,
	});
	const wrapped = response.data as any;
	const responseData = wrapped?.data !== undefined ? wrapped.data : wrapped;
	if (!responseData) {
		return null;
	}
	return mapBackendToEvaluationState(responseData);
};

export const createEvaluation = async (
	rfxId: number,
	panelMode?: PanelMode
): Promise<EvaluationState> => {
	const response = await apiV2.post(
		`/rfx/${rfxId}/evaluation/create`,
		panelMode ? { panel_mode: panelMode } : {}
	);
	const responseData = (response.data as any)?.data ?? response.data;
	return mapBackendToEvaluationState(responseData);
};

export const startEvaluation = async (
	payload: StartEvaluationPayload
): Promise<EvaluationState> => {
	const response = await apiV2.post(
		`/rfx/${payload.rfxId}/evaluation/start`,
		payload
	);
	const responseData = (response.data as any)?.data ?? response.data;
	return mapBackendToEvaluationState(responseData);
};

export const updateEvaluationStep = async (
	payload: UpdateEvaluationStepPayload
): Promise<EvaluationState> => {
	const response = await apiV2.patch(
		`/rfx/${payload.rfxId}/evaluation/step/${payload.stepId}`,
		payload
	);
	const responseData = (response.data as any)?.data ?? response.data;
	return mapBackendToEvaluationState(responseData);
};

export const changeEvaluationMode = async (
	rfxId: number,
	evaluationId: number,
	newMode: PanelMode
): Promise<EvaluationState> => {
	const response = await apiV2.patch(
		`/rfx/${rfxId}/evaluation/${evaluationId}/mode`,
		{ panel_mode: newMode }
	);
	const responseData = (response.data as any)?.data ?? response.data;
	return mapBackendToEvaluationState(responseData);
};

export const updateEvaluationProgress = async (
	rfxId: number,
	fields: { current_step?: number; panel_mode?: PanelMode }
): Promise<EvaluationState> => {
	const response = await apiV2.patch(
		`/rfx/${rfxId}/evaluation/progress`,
		fields
	);
	const responseData = (response.data as any)?.data ?? response.data;
	return mapBackendToEvaluationState(responseData);
};
